import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log('Form submitted:', formData);
    setSubmitted(true);
    setIsSubmitting(false);

    setTimeout(() => setSubmitted(false), 5000);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  // Intersection Observer for scroll reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const registerRef = (key) => (el) => {
    if (el) sectionRefs.current[key] = el;
  };

  // Social icon SVG paths (reused across the page + per-centre)
  const socialIcons = {
    facebook: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    instagram: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    linkedin: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    )
  };

  const regionalOffices = [
    {
      id: 'quetta',
      city: 'Quetta',
      region: 'Balochistan',
      regionFull: 'Balochistan',
      address: 'Industrial Estate, In-front of Utility Store Zonal Office, Sirki Road, Quetta',
      tel: '081-9211088',
      mobile: '+92 302 384-4602',
      email: 'Quetta@pitac.gov.pk',
      accent: 'emerald',
      image: '/images/regional/quetta.jpg',
      social: {
        facebook: 'https://www.facebook.com/share/1Bxgfpa8GX/',
        twitter: '#',
        instagram: '#',
        linkedin: '#'
      }
    },
    {
      id: 'mirpur',
      city: 'Mirpur',
      region: 'AJK',
      regionFull: 'Azad Jammu & Kashmir',
      address: 'House No. 141, Sector D-1, Allama Iqbal Road, Mirpur (AJK)',
      tel: '05827-920322',
      mobile: '',
      email: 'mirpur@pitac.gov.pk',
      accent: 'sky',
      image: '/images/regional/ajk.jpg',
      social: {
        facebook: '#',
        twitter: '#',
        instagram: '#',
        linkedin: '#'
      }
    },
    {
      id: 'gilgit',
      city: 'Gilgit',
      region: 'GB',
      regionFull: 'Gilgit-Baltistan',
      address: 'Building of Kashrote A Class Dispensary, Ehsan Ali Road, Kashrote, Gilgit (GB)',
      tel: '05811-942539',
      mobile: '',
      email: 'gilgit@pitac.gov.pk',
      accent: 'amber',
      image: '/images/regional/gilgit.jpg',
      social: {
        facebook: '#',
        twitter: '#',
        instagram: '#',
        linkedin: '#'
      }
    },
    {
      id: 'peshawar',
      city: 'Peshawar',
      region: 'KPK',
      regionFull: 'Khyber Pakhtunkhwa',
      address: 'Inside Govt. College of Technology, Kohat Road, Peshawar (KPK)',
      tel: '091-9210976',
      mobile: '',
      email: 'peshawar@pitac.gov.pk',
      accent: 'rose',
      image: '/images/regional/kpk.jpg',
      social: {
        facebook: '#',
        twitter: '#',
        instagram: '#',
        linkedin: '#'
      }
    },
    {
      id: 'karachi',
      city: 'Karachi',
      region: 'Sindh',
      regionFull: 'Sindh',
      address: 'House # 1/2 N, PECHS Block-06, Karachi (Sindh)',
      tel: '021-99333324',
      mobile: '',
      email: 'karachi@pitac.gov.pk',
      accent: 'violet',
      image: '/images/regional/karachi.jpg',
      social: {
        facebook: '#',
        twitter: '#',
        instagram: '#',
        linkedin: '#'
      }
    }
  ];

  return (
    <div className="contact-page">
      {/* ===== HERO SECTION ===== */}
      <section className="contact-hero">
        <div className="hero-bg-pattern" aria-hidden="true"></div>
        <div className="hero-orb hero-orb-1" aria-hidden="true"></div>
        <div className="hero-orb hero-orb-2" aria-hidden="true"></div>

        <div className="container hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            PITAC Pakistan
          </div>
          <h1 className="contact-hero-title">
            Let's <span className="title-accent">Connect</span>
          </h1>
          <p className="contact-hero-subtitle">
            Have a question about our programs, trainings, or regional services?
            Our team is here to help you every step of the way.
          </p>

          <div className="hero-quick-stats">
            <div className="quick-stat">
              <div className="quick-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <div>
                <div className="quick-stat-value">5</div>
                <div className="quick-stat-label">Regional Centres</div>
              </div>
            </div>
            <div className="quick-stat">
              <div className="quick-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div>
                <div className="quick-stat-value">24/7</div>
                <div className="quick-stat-label">Support Available</div>
              </div>
            </div>
            <div className="quick-stat">
              <div className="quick-stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div>
                <div className="quick-stat-value">100%</div>
                <div className="quick-stat-label">Response Rate</div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" />
          </svg>
        </div>
      </section>

      {/* ===== MAIN CONTENT ===== */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">

            {/* LEFT COLUMN — CONTACT INFO + SEND US A MESSAGE FORM */}
            <div className="contact-left-column">

              {/* Contact Information Card */}
              <div
                className={`contact-info-card reveal ${visibleSections['info'] ? 'visible' : ''}`}
                ref={registerRef('info')}
                data-section="info"
              >
                <div className="card-header">
                  <div className="card-header-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div className="card-header-text">
                    <h2 className="contact-info-title">Contact Information</h2>
                    <p className="contact-info-subtitle">Reach out through any of these channels</p>
                  </div>
                </div>

                <div className="contact-details-list">
                  <div className="contact-detail-item">
                    <div className="contact-detail-icon location">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                    </div>
                    <div className="contact-detail-content">
                      <h4>Head Office</h4>
                      <p>234-Ferozepur Road, near Metro Canal Station</p>
                      <p>Lahore 54600, Pakistan</p>
                    </div>
                  </div>

                  <div className="contact-detail-item">
                    <div className="contact-detail-icon phone">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    </div>
                    <div className="contact-detail-content">
                      <h4>Call Us</h4>
                      <p><a href="tel:+924299230699">+92 42 9923 0699</a></p>
                      <p><a href="tel:+924299230702">+92 42 9923 0702</a></p>
                    </div>
                  </div>

                  <div className="contact-detail-item">
                    <div className="contact-detail-icon email">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="M22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                      </svg>
                    </div>
                    <div className="contact-detail-content">
                      <h4>Email Us</h4>
                      <p><a href="mailto:info@pitac.gov.pk">info@pitac.gov.pk</a></p>
                      <p><a href="mailto:support@pitac.gov.pk">support@pitac.gov.pk</a></p>
                    </div>
                  </div>

                  <div className="contact-detail-item">
                    <div className="contact-detail-icon hours">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                    </div>
                    <div className="contact-detail-content">
                      <h4>Working Hours</h4>
                      <p>Monday – Friday: 9:00 AM – 5:00 PM</p>
                      <p>Saturday – Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                <div className="social-links">
                  <h4>Follow Us</h4>
                  <div className="social-icons">
                    <a href="#" className="social-icon facebook" aria-label="Facebook">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                    <a href="#" className="social-icon twitter" aria-label="Twitter">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                    <a href="#" className="social-icon linkedin" aria-label="LinkedIn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a href="#" className="social-icon youtube" aria-label="YouTube">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Send Us a Message — Form */}
              <div
                className={`contact-form-wrapper reveal ${visibleSections['form'] ? 'visible' : ''}`}
                ref={registerRef('form')}
                data-section="form"
              >
                <div className="contact-form-card">
                  <div className="form-card-accent" aria-hidden="true"></div>

                  <div className="form-header">
                    <div className="form-header-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 2L11 13"/>
                        <path d="M22 2L15 22l-4-9-9-4z"/>
                      </svg>
                    </div>
                    <div className="form-header-text">
                      <h2 className="form-title">Send Us a Message</h2>
                      <p className="form-description">
                        Fill out the form and we'll get back to you within 24 hours.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">
                          Full Name <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                          </svg>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="John Doe"
                            className="form-input"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">
                          Email Address <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="M22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                          </svg>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="john@example.com"
                            className="form-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">
                        Subject <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16v16H4z"/>
                          <path d="M9 9h6v6H9z"/>
                        </svg>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder="How can we help you?"
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">
                        Message <span className="required">*</span>
                      </label>
                      <div className="input-wrapper textarea-wrapper">
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="6"
                          placeholder="Tell us about your inquiry in detail..."
                          className="form-textarea"
                        />
                        <div className="char-count">
                          {formData.message.length} / 1000
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span>
                          Sending Message...
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14"/>
                            <path d="M12 5l7 7-7 7"/>
                          </svg>
                        </>
                      )}
                    </button>

                    {submitted && (
                      <div className="success-message">
                        <div className="success-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                        </div>
                        <div className="success-content">
                          <strong>Message Sent Successfully!</strong>
                          <span>We'll get back to you within 24 hours.</span>
                        </div>
                      </div>
                    )}
                  </form>

                  <div className="form-footer-note">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <span>Your information is secure and will never be shared.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN — OUR REGIONAL CENTRES */}
            <div
              className={`regional-offices reveal ${visibleSections['offices'] ? 'visible' : ''}`}
              ref={registerRef('offices')}
              data-section="offices"
            >
              <div className="regional-header">
                <div className="regional-header-text">
                  <h3 className="regional-title">Our Regional Centres</h3>
                  <p className="regional-subtitle">
                    PITAC operates across all provinces and regions of Pakistan
                  </p>
                </div>
                <div className="regional-count">05</div>
              </div>

              <div className="offices-grid">
                {regionalOffices.map((office) => (
                  <div
                    key={office.id}
                    className={`office-card accent-${office.accent}`}
                  >
                    {/* Regional image banner */}
                    <div className="office-image">
                      <img
                        src={office.image}
                        alt={`${office.city} regional centre`}
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('office-image-fallback');
                        }}
                      />
                      <span className="office-badge office-badge-overlay">
                        {office.region}
                      </span>
                    </div>

                    <div className="office-card-body">
                      <h4 className="office-city">{office.city}</h4>
                      <p className="office-region-full">{office.regionFull}</p>

                      <div className="office-divider"></div>

                      {/* Address */}
                      <div className="office-detail">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span>{office.address}</span>
                      </div>

                      {/* Landline */}
                      <a
                        href={`tel:${office.tel.replace(/[^0-9+]/g, '')}`}
                        className="office-detail office-tel"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <span>{office.tel}</span>
                      </a>

                      {/* Mobile (only renders if provided) */}
                      {office.mobile && (
                        <a
                          href={`tel:${office.mobile.replace(/[^0-9+]/g, '')}`}
                          className="office-detail office-mobile"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                            <line x1="12" y1="18" x2="12.01" y2="18"/>
                          </svg>
                          <span>{office.mobile}</span>
                        </a>
                      )}

                      {/* Email (only renders if provided) */}
                      {office.email && (
                        <a
                          href={`mailto:${office.email}`}
                          className="office-detail office-email"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="M22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                          </svg>
                          <span>{office.email}</span>
                        </a>
                      )}

                      {/* Per-centre social icons */}
                      <div className="office-social">
                        {office.social.facebook && (
                          <a
                            href={office.social.facebook}
                            className="office-social-icon facebook"
                            aria-label={`${office.city} Facebook`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {socialIcons.facebook}
                          </a>
                        )}
                        {office.social.twitter && (
                          <a
                            href={office.social.twitter}
                            className="office-social-icon twitter"
                            aria-label={`${office.city} Twitter`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {socialIcons.twitter}
                          </a>
                        )}
                        {office.social.instagram && (
                          <a
                            href={office.social.instagram}
                            className="office-social-icon instagram"
                            aria-label={`${office.city} Instagram`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {socialIcons.instagram}
                          </a>
                        )}
                        {office.social.linkedin && (
                          <a
                            href={office.social.linkedin}
                            className="office-social-icon linkedin"
                            aria-label={`${office.city} LinkedIn`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {socialIcons.linkedin}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAP SECTION ===== */}
      <section className="map-section">
        <div className="container">
          <div className="map-header">
            <div className="map-header-badge">Head Office</div>
            <h2 className="map-title">Find Us Here</h2>
            <p className="map-subtitle">
              Visit our head office at 234-Ferozepur Road, Lahore
            </p>
          </div>

          <div className="map-container">
            <iframe
              title="PITAC Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3400.0!2d74.3145!3d31.5204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3919048e3f7a3e55%3A0x4b5e8f4c5c4b5e8f!2s234%20Ferozepur%20Rd%2C%20Lahore%2C%20Pakistan!5e0!3m2!1sen!2s!4v1641234567890!5m2!1sen!2s"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;